<template>
    <div>
        <transition appear name="fade" mode="out-in">
            <div class="stat-tile">
                <div v-if="icon" class="d-none d-md-block icon" :class="`icon--${icon}`">
                </div>

                <!--text-->
                <div class="text font-arial" :class="{ 'big-mg': !icon }">
                    <span class="d-md-none" v-html="mobileText"></span>
                    <span class="d-none d-md-inline" v-html="text"></span>
                </div>

                <!-- Time or number% or number -->
                <div v-if="time" class="number" :class="[ secondNumber ? 'big-mg': '', color ? `number--${color}` : '' ]">
                    {{ animatedHour }}:{{ animatedMinutes }}:{{ animatedSeconds }}
                </div>
                <div v-else-if="percentage" class="number" :class="[ secondNumber ? 'big-mg': '', color ? `number--${color}` : '' ]">
                    {{ animatedNumber }}%
                </div>
                <div v-else class="number" :class="[ secondNumber ? 'big-mg': '', color ? `number--${color}` : '' ]">
                    {{ animatedNumber }}
                </div>

                <!--second number -->
                <div v-if="secondNumber || secondNumber === 0" class="d-none d-md-inline second-number" :class="[ color ? `number--${color}` : '' ]">
                    ({{ animatedSecondNumber }})
                </div>

                <div class="percent-bar" :class="[ color ? `percent-bar--${color}` : '' ]">
                    <span v-if="time" class="percent font-arial">{{ animatedPercent }}% (Target: {{ targetHours }}:{{ targetMinutes }}:{{ targetSeconds }})</span>
                    <span v-else-if="percentage" class="percent font-arial">{{ animatedPercent }}% (Target: {{ targetFormatted }}%)</span>
                    <span v-else class="percent font-arial">{{ animatedPercent }}% (Target: {{ targetFormatted }})</span>
                    <div class="fill" :class="[ color ? `fill--${color}` : '' ]" :style="{ width: `${animatedWidth}%` }"></div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import { TweenLite, Power4 } from 'gsap';

    export default {
        props: [
            'mobileText',
            'text',
            'number',
            'secondNumber',
            'icon',
            'time',
            'percent',
            'target',
            'flip',
            'percentage',
            'color',
        ],
        data() {
            return {
                tweenedNumber: 0,
                tweenedSecondNumber: 0,
                tweenedHour: 0,
                tweenedMinutes: 0,
                tweenedSeconds: 0,
                tweenedPercent: 0,
                leftClicked: false,
                rightClicked: false,
                animationDuration: 1.8,
            };
        },
        methods: {
            clickLeft() {
                this.rightClicked = false;

                if (this.leftClicked) {
                    this.leftClicked = false;
                } else {
                    this.leftClicked = true;
                }
            },
            clickRight() {
                this.leftClicked = false;

                if (this.rightClicked) {
                    this.rightClicked = false;
                } else {
                    this.rightClicked = true;
                }
            },
        },
        computed: {
            targetFormatted() {
                return this.target.toLocaleString('en-GB', { useGrouping: true });
            },
            targetHours() {
                return this.target.hours.toLocaleString('en-GB', { minimumIntegerDigits: 2, maximumFractionDigits: 0 });
            },
            targetMinutes() {
                return this.target.minutes.toLocaleString('en-GB', { minimumIntegerDigits: 2, maximumFractionDigits: 0 });
            },
            targetSeconds() {
                return this.target.seconds.toLocaleString('en-GB', { minimumIntegerDigits: 2, maximumFractionDigits: 0 });
            },
            animatedNumber() {
                const format = {
                    useGrouping: true,
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                };

                if (this.number >= 1) {
                    format.maximumFractionDigits = 0;
                    format.minimumFractionDigits = 0;
                }

                return this.tweenedNumber.toLocaleString('en-GB', format);
            },
            animatedSecondNumber() {
                const format = {
                    useGrouping: true,
                    maximumFractionDigits: 2,
                };

                if (this.secondNumber >= 1) {
                    format.maximumFractionDigits = 0;
                }

                return this.tweenedSecondNumber.toLocaleString('en-GB', format);
            },
            animatedHour() {
                return this.tweenedHour.toLocaleString('en-GB', { minimumIntegerDigits: 2, maximumFractionDigits: 0 });
            },
            animatedMinutes() {
                return this.tweenedMinutes.toLocaleString('en-GB', { minimumIntegerDigits: 2, maximumFractionDigits: 0 });
            },
            animatedSeconds() {
                return this.tweenedSeconds.toLocaleString('en-GB', { minimumIntegerDigits: 2, maximumFractionDigits: 0 });
            },
            animatedPercent() {
                return this.tweenedPercent.toFixed(0);
            },
            animatedWidth() {
                return this.tweenedPercent.toFixed(3);
            },
        },
        watch: {
            number(newValue) {
                TweenLite.to(this.$data, this.animationDuration, {
                    tweenedNumber: newValue,
                    ease: Power4.easeInOut,
                });
            },
            secondNumber(newValue) {
                TweenLite.to(this.$data, this.animationDuration, {
                    tweenedSecondNumber: newValue,
                    ease: Power4.easeInOut,
                });
            },
            time(newValue) {
                TweenLite.to(this.$data, this.animationDuration, {
                    tweenedHour: newValue.hours,
                    tweenedMinutes: newValue.minutes,
                    tweenedSeconds: newValue.seconds,
                    ease: Power4.easeInOut,
                });
            },
            percent(newValue) {
                const cappedValue = newValue > 100 ? 100 : newValue;

                TweenLite.to(this.$data, this.animationDuration, {
                    tweenedPercent: cappedValue,
                    ease: Power4.easeInOut,
                });
            },
        },
        mounted() {
            TweenLite.to(this.$data, this.animationDuration, {
                tweenedNumber: this.number,
                ease: Power4.easeInOut,
            });

            if (this.secondNumber) {
                TweenLite.to(this.$data, this.animationDuration, {
                    tweenedSecondNumber: this.secondNumber,
                    ease: Power4.easeInOut,
                });
            }

            if (this.time) {
                TweenLite.to(this.$data, this.animationDuration, {
                    tweenedHour: this.time.hours,
                    tweenedMinutes: this.time.minutes,
                    tweenedSeconds: this.time.seconds,
                    ease: Power4.easeInOut,
                });
            }

            const cappedValue = this.percent > 100 ? 100 : this.percent;

            TweenLite.to(this.$data, this.animationDuration, {
                tweenedPercent: cappedValue,
                ease: Power4.easeInOut,
            });
        },
    };
</script>

<style lang="scss" scoped>
    @import '../../assets/scss/partials/variables';

    .percent-bar {
        position: absolute;
        bottom: 0;
        left: 0;

        width: 100%;
        height: 14px;

        transition: height 0.6s ease;

        @media screen and (min-width: $tablet) {
            height: 24px;
        }

        &--green {
            background-color: transparentize($tileGreen-accent, 0.4);
        }
        &--blue {
            background-color: transparentize($tileBlue-accent, 0.4);
        }
        &--teal {
            background-color: transparentize($tileTeal-accent, 0.4);
        }
        &--pink {
            background-color: transparentize($tilePink-accent, 0.4);
        }
        &--purple {
            background-color: transparentize($tilePurple-accent, 0.4);
        }
        &--orange {
            background-color: transparentize($tileOrange-accent, 0.4);
        }

        .percent {
            position: absolute;
            left: 0;
            width: 100%;
            color: white;
            font-size: 10px;
            line-height: 14px;

            @media screen and (min-width: $tablet) {
                font-size: 16px;
                line-height: 24px;
            }
        }

        .fill {
            height: 100%;

            &--green {
                background-color: $tileGreen-accent;
            }
            &--blue {
                background-color: $tileBlue-accent;
            }
            &--teal {
                background-color: $tileTeal-accent;
            }
            &--pink {
                background-color: $tilePink-accent;
            }
            &--purple {
                background-color: $tilePurple-accent;
            }
            &--orange {
                background-color: $tileOrange-accent;
            }
        }
    }

    .stat-tile {
        padding: 9px 10px 21px;

        text-align: center;

        @media screen and (min-width: $tablet) {
            padding: 21px 10px 39px;
        }

        @media screen and (min-width: $desktop) {
            padding: 31px 10px 37px;
        }
    }

    .icon {
        margin-bottom: 13px;
    }

    .text {
        margin-bottom: 2px;
        font-size: 10px;
        line-height: 1.2;
        letter-spacing: 0.5px;
        color: $greyish-brown;

        @media screen and (min-width: $tablet) {
            margin-bottom: 6px;
        }

        @media screen and (min-width: $desktop) {
            margin-bottom: -6px;
            font-size: 16px;
            letter-spacing: 0.9px;
        }

        &.big-mg {
            @media screen and (min-width: $tablet) {
                margin-bottom: 11px;
            }
            @media screen and (min-width: $desktop) {
                margin-bottom: 2px;
            }
        }
    }

    .number {
        font-size: 35px;

        @media screen and (min-width: $tablet) {
            font-size: 50px;
        }

        @media screen and (min-width: $desktop) {
            font-size: 80px;
        }

        &--green {
            color: $tileGreen;
        }
        &--blue {
            color: $tileBlue;
        }
        &--teal {
            color: $tileTeal;
        }
        &--pink {
            color: $tilePink;
        }
        &--purple {
            color: $tilePurple;
        }
        &--orange {
            color: $tileOrange;
        }
    }

    .second-number {
        font-size: 30px;
    }
</style>



// WEBPACK FOOTER //
// src/components/tiles/StatTile.vue